import { Box, Dialog, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import React, { useContext, useState } from 'react';
import { useAppSelector } from '../reduxhooks';
import { ProductPageContext } from './ProductPageContext';
import PageProductOverlayNewReview from './PageProductOverlayNewReview';
import { ReviewElement, StarsElementRate } from './ProductDetailShared';

// Reviews for THIS product, built on the terbebas Review model
// (GET /storefront/reviews?productId=, POST /storefront/reviews) via
// ProductReviewsRedux.
//
// This replaces the inherited fareast section, which showed a store-wide
// "REVIEW UNTUK {kategori}" average and review wall on every product page
// regardless of what was being viewed.

export interface BlockProductReviewsData {
  heading?: string;
  // How many reviews to show before "Lihat semua".
  maxVisible?: number;
  showPhotos?: boolean;
  allowWrite?: boolean;
  fullWidth?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
createStyles({
  root: {
    padding: '0px 30px 30px 26px',
    [theme.breakpoints.down('xs')]: {
      padding: '0px 20px 24px 20px',
    },
  },
  heading: {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: '18px',
    color: '#121212',
    marginBottom: '10px',
  },
  summary: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  summaryScore: {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: '26px',
    color: '#121212',
    lineHeight: 1,
  },
  summaryMeta: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: '13px',
    color: '#6c6c6c',
  },
  photoStrip: {
    display: 'flex',
    gap: '8px',
    overflowX: 'auto',
    marginBottom: '18px',
    paddingBottom: '4px',
  },
  photo: {
    width: '84px',
    height: '84px',
    objectFit: 'cover',
    borderRadius: '6px',
    flexShrink: 0,
    cursor: 'pointer',
  },
  empty: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: '14px',
    color: '#6c6c6c',
    marginBottom: '14px',
  },
  actions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    marginTop: '4px',
  },
  button: {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: '12px',
    letterSpacing: '0.6px',
    color: '#121212',
    border: '1px solid #121212',
    borderRadius: '24px',
    padding: '9px 20px 7px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  dialogBody: {
    padding: '22px 26px 30px',
    minWidth: '320px',
    [theme.breakpoints.down('xs')]: {
      padding: '18px 18px 24px',
    },
  },
  dialogHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '14px',
  },
  closeIcon: {
    cursor: 'pointer',
  },
  lightboxImage: {
    display: 'block',
    maxWidth: '92vw',
    maxHeight: '86vh',
  },
}),
);

export default function BlockProductReviews({ content }: { content: BlockProductReviewsData }) {
  const classes = useStyles();
  const page = useContext(ProductPageContext);
  const reviews = useAppSelector((state) => state.productReviews.reviews);
  const averageStars = useAppSelector((state) => state.productReviews.averageStars);
  const fetchedFor = useAppSelector((state) => state.productReviews.fetchProductId);

  const [seeAllOpen, setSeeAllOpen] = useState(false);
  const [writeOpen, setWriteOpen] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);

  if (!page) return null;

  const {
    heading = 'ULASAN PRODUK', maxVisible = 5,
    showPhotos = true, allowWrite = true,
  } = content ?? {};

  // The store holds one product's reviews at a time; ignore it until it
  // holds THIS product's, or a stale set would render under a new title.
  const ready = fetchedFor === page.productId;
  const list = ready ? reviews : [];
  const visible = list.slice(0, Math.max(1, Number(maxVisible) || 5));
  const score = Math.round((averageStars ?? 0) * 10) / 10;

  const photos: string[] = [];
  if (showPhotos) {
    for (const review of list) {
      for (const url of review.imageUrls ?? []) photos.push(url);
    }
  }

  return (
    <Box className={classes.root} id="tb-product-reviews">
      <Typography className={classes.heading}>{heading}</Typography>

      {list.length > 0 &&
        <Box className={classes.summary}>
          <Typography className={classes.summaryScore}>{score}</Typography>
          <StarsElementRate stars={score} withText={false} />
          <Typography className={classes.summaryMeta}>{list.length} ulasan</Typography>
        </Box>}

      {showPhotos && photos.length > 0 &&
        <Box className={classes.photoStrip}>
          {photos.slice(0, 12).map((url, i) => (
            <img key={i} src={url} alt={`Foto ulasan ${i + 1}`} className={classes.photo}
              onClick={() => { if (!page.isPreview) setLightbox(url); }} />
          ))}
        </Box>}

      {list.length === 0 &&
        <Typography className={classes.empty}>
          Belum ada ulasan untuk produk ini.
        </Typography>}

      {visible.map((review, i) => (
        <ReviewElement
          key={review.creationTimestamp + '-' + i}
          name={review.name}
          isVerified={review.isVerified}
          stars={review.stars}
          creationTimestamp={review.creationTimestamp}
          headline={review.headline}
          message={review.message} />
      ))}

      <Box className={classes.actions}>
        {list.length > visible.length &&
          <Typography className={classes.button} id="tb-product-reviews-all"
            onClick={() => { if (!page.isPreview) setSeeAllOpen(true); }}>
            LIHAT SEMUA ULASAN ({list.length})
          </Typography>}
        {allowWrite &&
          <Typography className={classes.button} id="tb-product-reviews-write"
            onClick={() => { if (!page.isPreview) setWriteOpen(true); }}>
            TULIS ULASAN
          </Typography>}
      </Box>

      <Dialog open={seeAllOpen} onClose={() => setSeeAllOpen(false)} maxWidth="sm" fullWidth>
        <Box className={classes.dialogBody}>
          <Box className={classes.dialogHead}>
            <Typography className={classes.heading} style={{ marginBottom: 0 }}>
              {list.length} ulasan
            </Typography>
            <CloseIcon className={classes.closeIcon} onClick={() => setSeeAllOpen(false)} />
          </Box>
          {list.map((review, i) => (
            <ReviewElement
              key={'all-' + review.creationTimestamp + '-' + i}
              name={review.name}
              isVerified={review.isVerified}
              stars={review.stars}
              creationTimestamp={review.creationTimestamp}
              headline={review.headline}
              message={review.message} />
          ))}
        </Box>
      </Dialog>

      <Dialog open={!!lightbox} onClose={() => setLightbox(null)} maxWidth={false}>
        {!!lightbox && <img src={lightbox} alt="Foto ulasan" className={classes.lightboxImage} />}
      </Dialog>

      {allowWrite &&
        <PageProductOverlayNewReview
          productId={page.productId}
          newReviewOverlayOpen={writeOpen}
          setNewReviewOverlayOpen={setWriteOpen} />}
    </Box>
  );
}
